/**
 * WCAG contrast helpers for post-style-picker preview hints.
 * Mirrors server/server/utils/style-sanitize.js contrast logic.
 */

const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const RGB_COLOR = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;
const HSL_COLOR = /^hsl\(\s*(\d{1,3})\s*,\s*(\d{1,3})%\s*,\s*(\d{1,3})%\s*\)$/;

function clampComponent(value, max = 255) {
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return Math.min(max, Math.max(0, n));
}

function hexToRgb(hex) {
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized.split('').map((c) => c + c).join('');
  }
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
}

function parseColorToRgb(color) {
  if (!color || typeof color !== 'string') return null;
  const trimmed = color.trim();

  if (HEX_COLOR.test(trimmed)) {
    return hexToRgb(trimmed);
  }

  const rgbMatch = trimmed.match(RGB_COLOR);
  if (rgbMatch) {
    const r = clampComponent(rgbMatch[1]);
    const g = clampComponent(rgbMatch[2]);
    const b = clampComponent(rgbMatch[3]);
    if (r === null || g === null || b === null) return null;
    return { r, g, b };
  }

  const hslMatch = trimmed.match(HSL_COLOR);
  if (hslMatch) {
    const h = clampComponent(hslMatch[1], 360);
    const s = clampComponent(hslMatch[2], 100);
    const l = clampComponent(hslMatch[3], 100);
    if (h === null || s === null || l === null) return null;
    const sNorm = s / 100;
    const lNorm = l / 100;
    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lNorm - c / 2;
    let r1 = 0; let g1 = 0; let b1 = 0;
    if (h < 60) { r1 = c; g1 = x; }
    else if (h < 120) { r1 = x; g1 = c; }
    else if (h < 180) { g1 = c; b1 = x; }
    else if (h < 240) { g1 = x; b1 = c; }
    else if (h < 300) { r1 = x; b1 = c; }
    else { r1 = c; b1 = x; }
    return {
      r: Math.round((r1 + m) * 255),
      g: Math.round((g1 + m) * 255),
      b: Math.round((b1 + m) * 255)
    };
  }

  return null;
}

function relativeLuminance({ r, g, b }) {
  const transform = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * transform(r) + 0.7152 * transform(g) + 0.0722 * transform(b);
}

export function contrastRatio(fgRgb, bgRgb) {
  const l1 = relativeLuminance(fgRgb);
  const l2 = relativeLuminance(bgRgb);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function meetsWcagAA(fgColor, bgColor) {
  const fgRgb = parseColorToRgb(fgColor);
  const bgRgb = parseColorToRgb(bgColor);
  if (!fgRgb || !bgRgb) return false;
  return contrastRatio(fgRgb, bgRgb) >= 4.5;
}

export const STYLE_PRESET_COLORS = {
  'retro-terminal': { fgColor: '#00ff00', bgColor: '#1a1a1a' },
  'pastel-dreams': { fgColor: '#5c4d7a', bgColor: '#fce4ec' },
  'neon-nights': { fgColor: '#f0f0ff', bgColor: '#1a0a2e' },
  'fourth-of-july': { fgColor: '#010238', bgColor: '#FCFBFF' }
};

export function resolveStyleColors(style) {
  if (!style) return { fgColor: '#000000', bgColor: '#ffffff' };
  const preset = style.preset ? STYLE_PRESET_COLORS[style.preset] : null;
  return {
    fgColor: style.fgColor || preset?.fgColor || '#000000',
    bgColor: style.bgColor || preset?.bgColor || '#ffffff'
  };
}
